import React, { useState } from 'react';
import { PlusIcon, SearchIcon, EditIcon, TrashIcon, EyeIcon, PhoneIcon, MailIcon, MapPinIcon, FileTextIcon, CalendarIcon, XIcon, DownloadIcon, FileSpreadsheetIcon, UsersIcon, LockIcon, ShieldIcon } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { Textarea } from '../ui/Textarea';
import { Modal } from '../ui/Modal';
import { Badge, getStatusBadgeVariant } from '../ui/Badge';
import { Tabs, TabPanel } from '../ui/Tabs';
import { useData } from '../../contexts/DataContext';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';
import { Patient, Gender, MedicalHistory } from '../../types';
import { formatDate, formatTime, getGenderName, getStatusName } from '../../utils/helpers';
import { validatePatientForm } from '../../utils/validation';
import { exportPatientsToExcel } from '../../utils/excelExport';
import { usePagination } from '../../hooks/usePagination';
import { Pagination } from '../ui/Pagination';
export function Patients() {
  const {
    patients,
    addPatient,
    updatePatient,
    deletePatient,
    getPatient,
    updateMedicalHistory,
    insurances,
    getInsurance,
    getAppointmentsByPatient,
    getDoctor,
    getService
  } = useData();
  const {
    canCreate,
    canEdit,
    canDelete,
    isReadOnly
  } = usePermissions();
  const readOnly = isReadOnly('patients');
  const {
    user
  } = useAuth();
  const isAdmin = user?.role === 'administrador';
  const [searchTerm, setSearchTerm] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [activeTab, setActiveTab] = useState('info');
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isSavingMedical, setIsSavingMedical] = useState(false);
  const [medicalSaveSuccess, setMedicalSaveSuccess] = useState(false);
  const [medicalSaveError, setMedicalSaveError] = useState('');
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    age: 0,
    gender: 'masculino' as Gender,
    phone: '',
    email: '',
    address: '',
    emergencyContactName: '',
    emergencyContactRelationship: '',
    emergencyContactPhone: '',
    insuranceId: ''
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  // Medical history form state
  const [medicalData, setMedicalData] = useState<MedicalHistory | null>(null);
  const filteredPatients = patients.filter(p => p.name.toLowerCase().includes(searchTerm.toLowerCase()) || p.email.toLowerCase().includes(searchTerm.toLowerCase()) || p.phone.includes(searchTerm));
  const {
    paginatedItems: paginatedPatients,
    currentPage,
    totalPages,
    totalItems,
    startIndex,
    endIndex,
    goToPage,
    nextPage,
    prevPage
  } = usePagination(filteredPatients);
  const genderOptions = [{
    value: 'masculino',
    label: 'Masculino'
  }, {
    value: 'femenino',
    label: 'Femenino'
  }, {
    value: 'otro',
    label: 'Otro'
  }];
  const insuranceOptions = [{
    value: '',
    label: 'Sin seguro'
  }, ...insurances.map(i => ({
    value: i.id,
    label: i.name
  }))];
  const openNewForm = () => {
    if (!canCreate('patients')) return;
    setSelectedPatient(null);
    setFormData({
      name: '',
      age: 0,
      gender: 'masculino',
      phone: '',
      email: '',
      address: '',
      emergencyContactName: '',
      emergencyContactRelationship: '',
      emergencyContactPhone: '',
      insuranceId: ''
    });
    setFormErrors({});
    setIsFormOpen(true);
  };
  const openEditForm = (patient: Patient) => {
    if (!canEdit('patients')) return;
    setSelectedPatient(patient);
    setFormData({
      name: patient.name,
      age: patient.age,
      gender: patient.gender,
      phone: patient.phone,
      email: patient.email,
      address: patient.address,
      emergencyContactName: patient.emergencyContact?.name || '',
      emergencyContactRelationship: patient.emergencyContact?.relationship || '',
      emergencyContactPhone: patient.emergencyContact?.phone || '',
      insuranceId: patient.insuranceId || ''
    });
    setFormErrors({});
    setIsViewOpen(false);
    setIsFormOpen(true);
  };
  const openView = (patient: Patient) => {
    setSelectedPatient(patient);
    // Ensure all nested medical history objects exist with defaults
    const defaultMedicalHistory: MedicalHistory = {
      initialInterview: {
        background: '',
        visitReason: '',
        allergies: '',
        currentMedications: '',
        familyHistory: ''
      },
      symptoms: {
        description: '',
        duration: '',
        severity: '',
        notes: ''
      },
      physicalExam: {
        bloodPressure: '',
        bloodSugar: '',
        heartRate: '',
        lungs: '',
        eyes: '',
        throat: '',
        abdomen: '',
        weight: '',
        height: '',
        temperature: ''
      },
      instruments: [],
      diagnosis: '',
      recommendations: '',
      entries: []
    };
    const history = patient.medicalHistory || {};
    setMedicalData({
      initialInterview: {
        ...defaultMedicalHistory.initialInterview,
        ...(history.initialInterview || {})
      },
      symptoms: {
        ...defaultMedicalHistory.symptoms,
        ...(history.symptoms || {})
      },
      physicalExam: {
        ...defaultMedicalHistory.physicalExam,
        ...(history.physicalExam || {})
      },
      instruments: history.instruments || [],
      diagnosis: history.diagnosis || '',
      recommendations: history.recommendations || '',
      entries: history.entries || []
    });
    setActiveTab('info');
    setIsViewOpen(true);
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const validation = validatePatientForm(formData);
    if (!validation.isValid) {
      setFormErrors(validation.errors);
      return;
    }
    const patientData = {
      name: capitalizeName(formData.name),
      age: formData.age,
      gender: formData.gender,
      phone: formData.phone,
      email: formData.email,
      address: formData.address,
      emergencyContact: {
        name: capitalizeName(formData.emergencyContactName),
        relationship: formData.emergencyContactRelationship,
        phone: formData.emergencyContactPhone
      },
      insuranceId: formData.insuranceId || null
    };
    if (selectedPatient) {
      updatePatient(selectedPatient.id, patientData);
    } else {
      addPatient(patientData);
    }
    setIsFormOpen(false);
  };
  const handleDelete = () => {
    if (!canDelete('patients')) return;
    if (selectedPatient) {
      deletePatient(selectedPatient.id);
      setIsDeleteConfirmOpen(false);
      setIsViewOpen(false);
    }
  };
  const saveMedicalHistory = async () => {
    if (readOnly) return;
    if (selectedPatient && medicalData) {
      setIsSavingMedical(true);
      setMedicalSaveError('');
      setMedicalSaveSuccess(false);
      try {
        await updateMedicalHistory(selectedPatient.id, medicalData);
        setMedicalSaveSuccess(true);
        setTimeout(() => setMedicalSaveSuccess(false), 3000);
      } catch (error: any) {
        console.error('❌ Error guardando historial médico:', error);
        setMedicalSaveError(error?.message || 'Error al guardar el historial médico');
      } finally {
        setIsSavingMedical(false);
      }
    }
  };
  const tabs = [{
    id: 'info',
    label: 'Información',
    icon: <FileTextIcon className="w-4 h-4" data-id="element-1457" />
  }, {
    id: 'history',
    label: 'Historial Médico',
    icon: <FileTextIcon className="w-4 h-4" data-id="element-1458" />
  }, {
    id: 'appointments',
    label: 'Citas',
    icon: <CalendarIcon className="w-4 h-4" data-id="element-1459" />
  }];
  const handleExportToExcel = async () => {
    setIsExporting(true);
    await new Promise(resolve => setTimeout(resolve, 500));
    exportPatientsToExcel(patients, (insuranceId: string | null) => {
      if (!insuranceId) return 'Sin seguro';
      const insurance = getInsurance(insuranceId);
      return insurance?.name || 'Sin seguro';
    });
    setIsExporting(false);
  };
  // Helper to capitalize each word in a name
  const capitalizeName = (name: string) => {
    return name.trim().split(/\s+/).map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ');
  };
  return <div className="space-y-6" data-id="element-1460">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-1461">
        <div data-id="element-1462">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-1463">
            Pacientes
          </h1>
          <p className="text-gray-500 dark:text-gray-400" data-id="element-1464">
            Gestión de pacientes y expedientes médicos
            {readOnly && <span className="ml-2 text-amber-600 dark:text-amber-400" data-id="element-1465">
                (Solo lectura)
              </span>}
          </p>
        </div>
        {canCreate('patients') && <Button leftIcon={<PlusIcon className="w-4 h-4" data-id="element-1467" />} onClick={openNewForm} data-id="element-1466">
            Nuevo Paciente
          </Button>}
      </div>

      {/* Beautiful Export Card - Only for Admin */}
      {isAdmin && <Card className="bg-gradient-to-br from-sky-50 to-blue-50 dark:from-sky-950/30 dark:to-blue-950/30 border-sky-200 dark:border-sky-800" data-id="element-1468">
          <div className="flex items-center justify-between" data-id="element-1469">
            <div className="flex items-center gap-4" data-id="element-1470">
              <div className="w-14 h-14 rounded-xl bg-sky-500 dark:bg-sky-600 flex items-center justify-center shadow-lg shadow-sky-500/20" data-id="element-1471">
                <FileSpreadsheetIcon className="w-7 h-7 text-white" data-id="element-1472" />
              </div>
              <div data-id="element-1473">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1" data-id="element-1474">
                  Exportar Base de Datos
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400" data-id="element-1475">
                  Descarga todos los pacientes en formato Excel
                </p>
                <div className="flex items-center gap-4 mt-2" data-id="element-1476">
                  <div className="flex items-center gap-1.5 text-sm text-sky-700 dark:text-sky-400" data-id="element-1477">
                    <UsersIcon className="w-4 h-4" data-id="element-1478" />
                    <span className="font-medium" data-id="element-1479">
                      {patients.length} pacientes
                    </span>
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400" data-id="element-1480">
                    • Incluye: Nombre, Edad, Contacto, Seguro, Historial
                  </div>
                </div>
              </div>
            </div>
            <Button leftIcon={<DownloadIcon className="w-4 h-4" data-id="element-1482" />} onClick={handleExportToExcel} isLoading={isExporting} className="shadow-lg shadow-sky-500/20 hover:shadow-xl hover:shadow-sky-500/30 transition-all duration-200" data-id="element-1481">
              {isExporting ? 'Exportando...' : 'Descargar Excel'}
            </Button>
          </div>
        </Card>}

      {/* Search */}
      <Card data-id="element-1483">
        <div className="flex items-center gap-4" data-id="element-1484">
          <div className="flex-1 relative" data-id="element-1485">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-1486" />
            <Input placeholder="Buscar por nombre, correo o teléfono..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-10" data-id="element-1487" />
          </div>
        </div>
      </Card>

      {/* Patients List */}
      <Card padding="none" data-id="element-1488">
        <div className="overflow-x-auto" data-id="element-1489">
          <table className="w-full" data-id="element-1490">
            <thead data-id="element-1491">
              <tr className="border-b border-gray-200 dark:border-gray-700" data-id="element-1492">
                <th className="text-left py-4 px-6 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-1493">
                  Paciente
                </th>
                <th className="text-left py-4 px-6 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-1494">
                  Contacto
                </th>
                <th className="text-left py-4 px-6 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-1495">
                  Seguro
                </th>
                <th className="text-left py-4 px-6 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-1496">
                  Edad
                </th>
                <th className="text-right py-4 px-6 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase" data-id="element-1497">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700" data-id="element-1498">
              {filteredPatients.length === 0 ? <tr data-id="element-1499">
                  <td colSpan={5} className="py-12 text-center text-gray-500 dark:text-gray-400" data-id="element-1500">
                    No se encontraron pacientes
                  </td>
                </tr> : paginatedPatients.map(patient => {
              const insurance = patient.insuranceId ? getInsurance(patient.insuranceId) : null;
              return <tr key={patient.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50" data-id="element-1501">
                      <td className="py-4 px-6" data-id="element-1502">
                        <div data-id="element-1503">
                          <p className="font-medium text-gray-900 dark:text-white capitalize" data-id="element-1504">
                            {patient.name}
                          </p>
                          <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-1505">
                            {getGenderName(patient.gender)}
                          </p>
                        </div>
                      </td>
                      <td className="py-4 px-6" data-id="element-1506">
                        <div className="space-y-1" data-id="element-1507">
                          <p className="text-sm text-gray-900 dark:text-gray-100 flex items-center gap-1" data-id="element-1508">
                            <PhoneIcon className="w-3 h-3" data-id="element-1509" /> {patient.phone}
                          </p>
                          <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1" data-id="element-1510">
                            <MailIcon className="w-3 h-3" data-id="element-1511" /> {patient.email}
                          </p>
                        </div>
                      </td>
                      <td className="py-4 px-6" data-id="element-1512">
                        {insurance ? <Badge variant="info" data-id="element-1513">{insurance.name}</Badge> : <span className="text-sm text-gray-400" data-id="element-1514">
                            Sin seguro
                          </span>}
                      </td>
                      <td className="py-4 px-6 text-sm text-gray-900 dark:text-gray-100" data-id="element-1515">
                        {patient.age} años
                      </td>
                      <td className="py-4 px-6" data-id="element-1516">
                        <div className="flex items-center justify-end gap-2" data-id="element-1517">
                          <button onClick={() => openView(patient)} className="p-2 text-gray-500 hover:text-sky-600 hover:bg-sky-50 dark:hover:bg-sky-900/20 rounded-lg transition-colors" title="Ver detalles" data-id="element-1518">
                            <EyeIcon className="w-4 h-4" data-id="element-1519" />
                          </button>
                          {canEdit('patients') ? <button onClick={() => openEditForm(patient)} className="p-2 text-gray-500 hover:text-sky-600 hover:bg-sky-50 dark:hover:bg-sky-900/20 rounded-lg transition-colors" title="Editar" data-id="element-1520">
                              <EditIcon className="w-4 h-4" data-id="element-1521" />
                            </button> : <button disabled className="p-2 text-gray-300 dark:text-gray-600 cursor-not-allowed" title="Sin permisos para editar" data-id="element-1522">
                              <LockIcon className="w-4 h-4" data-id="element-1523" />
                            </button>}
                        </div>
                      </td>
                    </tr>;
            })}
            </tbody>
          </table>
        </div>
        {/* Pagination */}
        <div className="px-6 pb-4" data-id="element-1524">
          <Pagination currentPage={currentPage} totalPages={totalPages} totalItems={totalItems} startIndex={startIndex} endIndex={endIndex} onPageChange={goToPage} onNext={nextPage} onPrev={prevPage} data-id="element-1525" />
        </div>
      </Card>

      {/* Patient Form Modal */}
      <Modal isOpen={isFormOpen} onClose={() => setIsFormOpen(false)} title={selectedPatient ? 'Editar Paciente' : 'Nuevo Paciente'} size="lg" icon={<UsersIcon className="w-5 h-5" data-id="element-1527" />} accentColor="emerald" data-id="element-1526">
        <form onSubmit={handleSubmit} className="space-y-6" data-id="element-1528">
          {/* Personal Info Section */}
          <div data-id="element-1529">
            <div className="flex items-center gap-2 mb-4" data-id="element-1530">
              <span className="w-1.5 h-5 rounded-full bg-emerald-500" data-id="element-1531"></span>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide" data-id="element-1532">
                Información Personal
              </h3>
            </div>
            <div className="bg-gray-50 dark:bg-gray-900/30 rounded-xl p-4 space-y-4 border border-gray-100 dark:border-gray-700/50" data-id="element-1533">
              <div className="grid grid-cols-2 gap-4" data-id="element-1534">
                <Input label="Nombre Completo" value={formData.name} onChange={e => setFormData({
                ...formData,
                name: e.target.value
              })} error={formErrors.name} placeholder="Nombre del paciente" data-id="element-1535" />
                <div className="grid grid-cols-2 gap-4" data-id="element-1536">
                  <Input type="number" label="Edad" value={formData.age} onChange={e => setFormData({
                  ...formData,
                  age: parseInt(e.target.value) || 0
                })} error={formErrors.age} data-id="element-1537" />
                  <Select label="Sexo" options={genderOptions} value={formData.gender} onChange={value => setFormData({
                  ...formData,
                  gender: value as Gender
                })} error={formErrors.gender} data-id="element-1538" />
                </div>
              </div>
              <Input label="Dirección" value={formData.address} onChange={e => setFormData({
              ...formData,
              address: e.target.value
            })} placeholder="Dirección completa" data-id="element-1539" />
            </div>
          </div>

          {/* Contact Section */}
          <div data-id="element-1540">
            <div className="flex items-center gap-2 mb-4" data-id="element-1541">
              <span className="w-1.5 h-5 rounded-full bg-teal-500" data-id="element-1542"></span>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide" data-id="element-1543">
                Contacto
              </h3>
            </div>
            <div className="bg-gray-50 dark:bg-gray-900/30 rounded-xl p-4 space-y-4 border border-gray-100 dark:border-gray-700/50" data-id="element-1544">
              <div className="grid grid-cols-2 gap-4" data-id="element-1545">
                <Input label="Teléfono" value={formData.phone} onChange={e => setFormData({
                ...formData,
                phone: e.target.value
              })} error={formErrors.phone} placeholder="809-000-0000" data-id="element-1546" />
                <Input type="email" label="Correo Electrónico" value={formData.email} onChange={e => setFormData({
                ...formData,
                email: e.target.value
              })} error={formErrors.email} placeholder="paciente@email.com" data-id="element-1547" />
              </div>
            </div>
          </div>

          {/* Emergency Contact Section */}
          <div data-id="element-1548">
            <div className="flex items-center gap-2 mb-4" data-id="element-1549">
              <span className="w-1.5 h-5 rounded-full bg-rose-500" data-id="element-1550"></span>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide" data-id="element-1551">
                Contacto de Emergencia
              </h3>
            </div>
            <div className="bg-red-50/50 dark:bg-red-900/5 rounded-xl p-4 space-y-4 border border-red-100 dark:border-red-900/20" data-id="element-1552">
              <div className="grid grid-cols-2 gap-4" data-id="element-1553">
                <Input label="Nombre Completo" value={formData.emergencyContactName} onChange={e => setFormData({
                ...formData,
                emergencyContactName: e.target.value
              })} placeholder="Nombre del contacto" data-id="element-1554" />
                <Input label="Parentesco" value={formData.emergencyContactRelationship} onChange={e => setFormData({
                ...formData,
                emergencyContactRelationship: e.target.value
              })} placeholder="Ej: Madre, Esposo" data-id="element-1555" />
              </div>
              <Input label="Teléfono de Emergencia" value={formData.emergencyContactPhone} onChange={e => setFormData({
              ...formData,
              emergencyContactPhone: e.target.value
            })} placeholder="Número de contacto" data-id="element-1556" />
            </div>
          </div>

          {/* Insurance Section */}
          <div data-id="element-1557">
            <div className="flex items-center gap-2 mb-4" data-id="element-1558">
              <span className="w-1.5 h-5 rounded-full bg-sky-500" data-id="element-1559"></span>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide" data-id="element-1560">
                Seguro Médico
              </h3>
            </div>
            <div className="bg-gray-50 dark:bg-gray-900/30 rounded-xl p-4 border border-gray-100 dark:border-gray-700/50" data-id="element-1561">
              <Select label="Seguro Médico" options={insuranceOptions} value={formData.insuranceId} onChange={value => setFormData({
              ...formData,
              insuranceId: value
            })} data-id="element-1562" />
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-100 dark:border-gray-700" data-id="element-1563">
            <Button variant="outline" type="button" onClick={() => setIsFormOpen(false)} data-id="element-1564">
              Cancelar
            </Button>
            <Button type="submit" data-id="element-1565">
              {selectedPatient ? 'Guardar Cambios' : 'Crear Paciente'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Patient View Modal */}
      <Modal isOpen={isViewOpen} onClose={() => setIsViewOpen(false)} title="Expediente del Paciente" size="xl" data-id="element-1566">
        {selectedPatient && <div className="space-y-6" data-id="element-1567">
            {/* Hero Header */}
            <div className="relative rounded-xl overflow-hidden bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm" data-id="element-1568">
              <div className="h-20 bg-gradient-to-r from-emerald-500 to-teal-600 dark:from-emerald-600 dark:to-teal-700" data-id="element-1569"></div>
              <div className="px-6 pb-5 pt-0" data-id="element-1570">
                <div className="flex items-end gap-4 -mt-10" data-id="element-1571">
                  <div className="w-20 h-20 rounded-xl bg-white dark:bg-gray-800 p-1 shadow-lg flex-shrink-0" data-id="element-1572">
                    <div className="w-full h-full rounded-lg bg-emerald-100 dark:bg-emerald-900/50 flex items-center justify-center text-2xl font-bold text-emerald-600 dark:text-emerald-400" data-id="element-1573">
                      {selectedPatient.name.charAt(0).toUpperCase()}
                    </div>
                  </div>
                  <div className="flex-1 min-w-0 pb-1" data-id="element-1574">
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white capitalize truncate" data-id="element-1575">
                      {selectedPatient.name}
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400 font-medium" data-id="element-1576">
                      {selectedPatient.age} años &nbsp;•&nbsp;{' '}
                      <span className="capitalize" data-id="element-1577">
                        {getGenderName(selectedPatient.gender)}
                      </span>
                    </p>
                  </div>
                  <div className="flex gap-2 flex-shrink-0 pb-1" data-id="element-1578">
                    {!readOnly && <>
                        {canEdit('patients') && <Button variant="outline" size="sm" onClick={() => openEditForm(selectedPatient)} data-id="element-1579">
                            <EditIcon className="w-4 h-4 mr-2" data-id="element-1580" /> Editar
                          </Button>}
                        {canDelete('patients') && <Button variant="danger" size="sm" onClick={() => setIsDeleteConfirmOpen(true)} data-id="element-1581">
                            <TrashIcon className="w-4 h-4" data-id="element-1582" />
                          </Button>}
                      </>}
                  </div>
                </div>
              </div>
            </div>

            <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} data-id="element-1583">
              <TabPanel id="info" activeTab={activeTab} data-id="element-1584">
                <div className="space-y-6" data-id="element-1585">
                  {/* Contact Info Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1586">
                    <div className="p-4 rounded-xl bg-gray-50 dark:bg-gray-800/50 border border-gray-100 dark:border-gray-700" data-id="element-1587">
                      <div className="flex items-center gap-3 mb-3" data-id="element-1588">
                        <div className="w-8 h-8 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-blue-600 dark:text-blue-400" data-id="element-1589">
                          <PhoneIcon className="w-4 h-4" data-id="element-1590" />
                        </div>
                        <h4 className="font-semibold text-gray-900 dark:text-white" data-id="element-1591">
                          Teléfono
                        </h4>
                      </div>
                      <p className="text-gray-600 dark:text-gray-300 pl-11" data-id="element-1592">
                        {selectedPatient.phone}
                      </p>
                    </div>

                    <div className="p-4 rounded-xl bg-gray-50 dark:bg-gray-800/50 border border-gray-100 dark:border-gray-700" data-id="element-1593">
                      <div className="flex items-center gap-3 mb-3" data-id="element-1594">
                        <div className="w-8 h-8 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center text-purple-600 dark:text-purple-400" data-id="element-1595">
                          <MailIcon className="w-4 h-4" data-id="element-1596" />
                        </div>
                        <h4 className="font-semibold text-gray-900 dark:text-white" data-id="element-1597">
                          Correo
                        </h4>
                      </div>
                      <p className="text-gray-600 dark:text-gray-300 pl-11" data-id="element-1598">
                        {selectedPatient.email}
                      </p>
                    </div>

                    <div className="p-4 rounded-xl bg-gray-50 dark:bg-gray-800/50 border border-gray-100 dark:border-gray-700 md:col-span-2" data-id="element-1599">
                      <div className="flex items-center gap-3 mb-3" data-id="element-1600">
                        <div className="w-8 h-8 rounded-lg bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center text-orange-600 dark:text-orange-400" data-id="element-1601">
                          <MapPinIcon className="w-4 h-4" data-id="element-1602" />
                        </div>
                        <h4 className="font-semibold text-gray-900 dark:text-white" data-id="element-1603">
                          Dirección
                        </h4>
                      </div>
                      <p className="text-gray-600 dark:text-gray-300 pl-11" data-id="element-1604">
                        {selectedPatient.address}
                      </p>
                    </div>

                    <div className="p-4 rounded-xl bg-gray-50 dark:bg-gray-800/50 border border-gray-100 dark:border-gray-700 md:col-span-2" data-id="element-1605">
                      <div className="flex items-center gap-3 mb-3" data-id="element-1606">
                        <div className="w-8 h-8 rounded-lg bg-sky-100 dark:bg-sky-900/30 flex items-center justify-center text-sky-600 dark:text-sky-400" data-id="element-1607">
                          <ShieldIcon className="w-4 h-4" data-id="element-1608" />
                        </div>
                        <h4 className="font-semibold text-gray-900 dark:text-white" data-id="element-1609">
                          Seguro Médico
                        </h4>
                      </div>
                      <p className="text-gray-600 dark:text-gray-300 pl-11" data-id="element-1610">
                        {selectedPatient.insuranceId ? getInsurance(selectedPatient.insuranceId)?.name : 'Sin seguro'}
                      </p>
                    </div>
                  </div>

                  {/* Emergency Contact Section */}
                  <div className="rounded-xl border border-red-100 dark:border-red-900/30 bg-red-50/50 dark:bg-red-900/10 p-5" data-id="element-1611">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2" data-id="element-1612">
                      <span className="w-2 h-6 rounded-full bg-red-500" data-id="element-1613"></span>
                      Contacto de Emergencia
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-6" data-id="element-1614">
                      <div data-id="element-1615">
                        <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1" data-id="element-1616">
                          Nombre
                        </h4>
                        <p className="text-gray-900 dark:text-white font-medium" data-id="element-1617">
                          {selectedPatient.emergencyContact?.name || '-'}
                        </p>
                      </div>
                      <div data-id="element-1618">
                        <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1" data-id="element-1619">
                          Parentesco
                        </h4>
                        <p className="text-gray-900 dark:text-white font-medium" data-id="element-1620">
                          {selectedPatient.emergencyContact?.relationship || '-'}
                        </p>
                      </div>
                      <div data-id="element-1621">
                        <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1" data-id="element-1622">
                          Teléfono
                        </h4>
                        <p className="text-gray-900 dark:text-white font-medium flex items-center gap-2" data-id="element-1623">
                          <PhoneIcon className="w-4 h-4 text-red-500" data-id="element-1624" />
                          {selectedPatient.emergencyContact?.phone || '-'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </TabPanel>

              <TabPanel id="history" activeTab={activeTab} data-id="element-1625">
                {medicalData && <div className="space-y-6" data-id="element-1626">
                    {readOnly && <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg text-amber-700 dark:text-amber-300 text-sm flex items-center gap-2" data-id="element-1627">
                        <LockIcon className="w-4 h-4" data-id="element-1628" />
                        <span data-id="element-1629">
                          Solo tienes permisos de lectura. No puedes editar el
                          historial médico.
                        </span>
                      </div>}

                    {/* Initial Interview */}
                    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm" data-id="element-1630">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2" data-id="element-1631">
                        <div className="w-8 h-8 rounded-lg bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center text-indigo-600 dark:text-indigo-400" data-id="element-1632">
                          <FileTextIcon className="w-4 h-4" data-id="element-1633" />
                        </div>
                        Entrevista Inicial
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1634">
                        <Textarea label="Antecedentes" value={medicalData.initialInterview.background} onChange={e => setMedicalData({
                    ...medicalData,
                    initialInterview: {
                      ...medicalData.initialInterview,
                      background: e.target.value
                    }
                  })} rows={2} disabled={readOnly} data-id="element-1635" />
                        <Textarea label="Motivo de Visita" value={medicalData.initialInterview.visitReason} onChange={e => setMedicalData({
                    ...medicalData,
                    initialInterview: {
                      ...medicalData.initialInterview,
                      visitReason: e.target.value
                    }
                  })} rows={2} disabled={readOnly} data-id="element-1636" />
                        <Input label="Alergias" value={medicalData.initialInterview.allergies} onChange={e => setMedicalData({
                    ...medicalData,
                    initialInterview: {
                      ...medicalData.initialInterview,
                      allergies: e.target.value
                    }
                  })} disabled={readOnly} data-id="element-1637" />
                        <Input label="Medicamentos Actuales" value={medicalData.initialInterview.currentMedications} onChange={e => setMedicalData({
                    ...medicalData,
                    initialInterview: {
                      ...medicalData.initialInterview,
                      currentMedications: e.target.value
                    }
                  })} disabled={readOnly} data-id="element-1638" />
                        <Textarea label="Historial Familiar" value={medicalData.initialInterview.familyHistory} onChange={e => setMedicalData({
                    ...medicalData,
                    initialInterview: {
                      ...medicalData.initialInterview,
                      familyHistory: e.target.value
                    }
                  })} rows={2} className="md:col-span-2" disabled={readOnly} data-id="element-1639" />
                      </div>
                    </div>

                    {/* Symptoms */}
                    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm" data-id="element-1640">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2" data-id="element-1641">
                        <div className="w-8 h-8 rounded-lg bg-rose-100 dark:bg-rose-900/30 flex items-center justify-center text-rose-600 dark:text-rose-400" data-id="element-1642">
                          <FileTextIcon className="w-4 h-4" data-id="element-1643" />
                        </div>
                        Revisión de Síntomas
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1644">
                        <Textarea label="Descripción" value={medicalData.symptoms.description} onChange={e => setMedicalData({
                    ...medicalData,
                    symptoms: {
                      ...medicalData.symptoms,
                      description: e.target.value
                    }
                  })} rows={2} disabled={readOnly} data-id="element-1645" />
                        <div className="grid grid-cols-2 gap-4" data-id="element-1646">
                          <Input label="Duración" value={medicalData.symptoms.duration} onChange={e => setMedicalData({
                      ...medicalData,
                      symptoms: {
                        ...medicalData.symptoms,
                        duration: e.target.value
                      }
                    })} disabled={readOnly} data-id="element-1647" />
                          <Input label="Severidad" value={medicalData.symptoms.severity} onChange={e => setMedicalData({
                      ...medicalData,
                      symptoms: {
                        ...medicalData.symptoms,
                        severity: e.target.value
                      }
                    })} disabled={readOnly} data-id="element-1648" />
                        </div>
                      </div>
                    </div>

                    {/* Physical Exam */}
                    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm" data-id="element-1649">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2" data-id="element-1650">
                        <div className="w-8 h-8 rounded-lg bg-teal-100 dark:bg-teal-900/30 flex items-center justify-center text-teal-600 dark:text-teal-400" data-id="element-1651">
                          <FileTextIcon className="w-4 h-4" data-id="element-1652" />
                        </div>
                        Exploración Física
                      </h3>
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4" data-id="element-1653">
                        <Input label="Presión Arterial" value={medicalData.physicalExam.bloodPressure} onChange={e => setMedicalData({
                    ...medicalData,
                    physicalExam: {
                      ...medicalData.physicalExam,
                      bloodPressure: e.target.value
                    }
                  })} placeholder="120/80" disabled={readOnly} data-id="element-1654" />
                        <Input label="Azúcar en Sangre" value={medicalData.physicalExam.bloodSugar} onChange={e => setMedicalData({
                    ...medicalData,
                    physicalExam: {
                      ...medicalData.physicalExam,
                      bloodSugar: e.target.value
                    }
                  })} placeholder="mg/dL" disabled={readOnly} data-id="element-1655" />
                        <Input label="Ritmo Cardíaco" value={medicalData.physicalExam.heartRate} onChange={e => setMedicalData({
                    ...medicalData,
                    physicalExam: {
                      ...medicalData.physicalExam,
                      heartRate: e.target.value
                    }
                  })} placeholder="bpm" disabled={readOnly} data-id="element-1656" />
                        <Input label="Pulmones" value={medicalData.physicalExam.lungs} onChange={e => setMedicalData({
                    ...medicalData,
                    physicalExam: {
                      ...medicalData.physicalExam,
                      lungs: e.target.value
                    }
                  })} disabled={readOnly} data-id="element-1657" />
                        <Input label="Ojos" value={medicalData.physicalExam.eyes} onChange={e => setMedicalData({
                    ...medicalData,
                    physicalExam: {
                      ...medicalData.physicalExam,
                      eyes: e.target.value
                    }
                  })} disabled={readOnly} data-id="element-1658" />
                        <Input label="Garganta" value={medicalData.physicalExam.throat} onChange={e => setMedicalData({
                    ...medicalData,
                    physicalExam: {
                      ...medicalData.physicalExam,
                      throat: e.target.value
                    }
                  })} disabled={readOnly} data-id="element-1659" />
                        <Input label="Abdomen" value={medicalData.physicalExam.abdomen} onChange={e => setMedicalData({
                    ...medicalData,
                    physicalExam: {
                      ...medicalData.physicalExam,
                      abdomen: e.target.value
                    }
                  })} disabled={readOnly} data-id="element-1660" />
                        <Input label="Peso" value={medicalData.physicalExam.weight} onChange={e => setMedicalData({
                    ...medicalData,
                    physicalExam: {
                      ...medicalData.physicalExam,
                      weight: e.target.value
                    }
                  })} placeholder="kg" disabled={readOnly} data-id="element-1661" />
                        <Input label="Altura" value={medicalData.physicalExam.height} onChange={e => setMedicalData({
                    ...medicalData,
                    physicalExam: {
                      ...medicalData.physicalExam,
                      height: e.target.value
                    }
                  })} placeholder="cm" disabled={readOnly} data-id="element-1662" />
                        <Input label="Temperatura" value={medicalData.physicalExam.temperature} onChange={e => setMedicalData({
                    ...medicalData,
                    physicalExam: {
                      ...medicalData.physicalExam,
                      temperature: e.target.value
                    }
                  })} placeholder="°C" disabled={readOnly} data-id="element-1663" />
                      </div>
                    </div>

                    {/* Diagnosis */}
                    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm" data-id="element-1664">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2" data-id="element-1665">
                        <div className="w-8 h-8 rounded-lg bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center text-amber-600 dark:text-amber-400" data-id="element-1666">
                          <FileTextIcon className="w-4 h-4" data-id="element-1667" />
                        </div>
                        Diagnóstico y Recomendaciones
                      </h3>
                      <div className="space-y-4" data-id="element-1668">
                        <Textarea label="Diagnóstico" value={medicalData.diagnosis} onChange={e => setMedicalData({
                    ...medicalData,
                    diagnosis: e.target.value
                  })} rows={3} disabled={readOnly} data-id="element-1669" />
                        <Textarea label="Recomendaciones" value={medicalData.recommendations} onChange={e => setMedicalData({
                    ...medicalData,
                    recommendations: e.target.value
                  })} rows={3} disabled={readOnly} data-id="element-1670" />
                      </div>
                    </div>

                    {!readOnly && <div className="space-y-3" data-id="element-1671">
                        {medicalSaveError && <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-600 dark:text-red-400 text-sm" data-id="element-1672">
                            ❌ {medicalSaveError}
                          </div>}
                        {medicalSaveSuccess && <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg text-green-600 dark:text-green-400 text-sm" data-id="element-1673">
                            ✅ Historial médico guardado exitosamente
                          </div>}
                        <div className="flex justify-end pt-4" data-id="element-1674">
                          <Button onClick={saveMedicalHistory} isLoading={isSavingMedical} data-id="element-1675">
                            {isSavingMedical ? 'Guardando...' : 'Guardar Historial Médico'}
                          </Button>
                        </div>
                      </div>}
                  </div>}
              </TabPanel>

              <TabPanel id="appointments" activeTab={activeTab} data-id="element-1676">
                <div className="space-y-4" data-id="element-1677">
                  {getAppointmentsByPatient(selectedPatient.id).length === 0 ? <div className="text-center py-12 bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-dashed border-gray-200 dark:border-gray-700" data-id="element-1678">
                      <CalendarIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" data-id="element-1679" />
                      <p className="text-gray-500 dark:text-gray-400" data-id="element-1680">
                        No hay citas registradas
                      </p>
                    </div> : getAppointmentsByPatient(selectedPatient.id).map(apt => {
                const doctor = getDoctor(apt.doctorId);
                const service = getService(apt.serviceId);
                return <div key={apt.id} className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-xl hover:shadow-sm transition-shadow" data-id="element-1681">
                          <div className="flex items-center gap-4" data-id="element-1682">
                            <div className="w-10 h-10 rounded-full bg-emerald-50 dark:bg-emerald-900/20 flex items-center justify-center text-emerald-600 dark:text-emerald-400 font-bold" data-id="element-1683">
                              {doctor?.name.charAt(0)}
                            </div>
                            <div data-id="element-1684">
                              <p className="font-medium text-gray-900 dark:text-white" data-id="element-1685">
                                {apt.date} - {formatTime(apt.time)}
                              </p>
                              <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-1686">
                                Dr. {doctor?.name} • {service?.name}
                              </p>
                            </div>
                          </div>
                          <Badge variant={getStatusBadgeVariant(apt.status)} data-id="element-1687">
                            {getStatusName(apt.status)}
                          </Badge>
                        </div>;
              })}
                </div>
              </TabPanel>
            </Tabs>
          </div>}
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={isDeleteConfirmOpen} onClose={() => setIsDeleteConfirmOpen(false)} title="Confirmar Eliminación" size="sm" data-id="element-1688">
        <div className="space-y-4" data-id="element-1689">
          <p className="text-gray-600 dark:text-gray-400" data-id="element-1690">
            ¿Está seguro de que desea eliminar a este paciente? Esta acción no
            se puede deshacer.
          </p>
          <div className="flex justify-end gap-3" data-id="element-1691">
            <Button variant="outline" onClick={() => setIsDeleteConfirmOpen(false)} data-id="element-1692">
              Cancelar
            </Button>
            <Button variant="danger" onClick={handleDelete} data-id="element-1693">
              Eliminar
            </Button>
          </div>
        </div>
      </Modal>
    </div>;
}